import { Component } from '@theme/component';
import { ThemeHeaderHero } from '@theme/theme-header-hero';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle, calculateHeaderGroupHeight } from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #heroHeader = null;

  #topRow = null;

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  // Accumulate intentional movement instead of reacting to trackpad jitter.
  #scrollDirection = 0;
  #directionStart = 0;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const size = entry.borderBoxSize?.[0];
      if (!size) continue;

      // Delivered border boxes are already measured, including the top row.
      // Reading geometry after a global height write would force layout again.
      if (entry.target === this) {
        const height = `${Math.round(size.blockSize)}px`;
        if (document.body.style.getPropertyValue('--header-height') !== height) {
          document.body.style.setProperty('--header-height', height);
        }
      } else if (entry.target === this.#topRow) {
        const height = `${size.blockSize}px`;
        if (this.style.getPropertyValue('--top-row-height') !== height) {
          this.style.setProperty('--top-row-height', height);
        }
      }
    }

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // The shared helper has already transferred/clamped the new owner's offset.
    this.#lastScrollTop = getScrollTop();
    this.#directionStart = this.#lastScrollTop;
    this.#scrollDirection = 0;

    // Only always-sticky mode needs an observer; scroll-up stays sticky in CSS.
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    if (stickyMode === 'always') this.#observeStickyPosition();
    else this.#updateScrollState();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = (scrollTop = Math.max(0, getScrollTop())) => {
    const stickyMode = this.getAttribute('sticky');
    const previousScrollTop = this.#lastScrollTop;
    const delta = scrollTop - previousScrollTop;
    this.#lastScrollTop = scrollTop;

    // Size follows page position, independently of visibility and scroll direction.
    const scrolled = String(scrollTop > 8 || (this.dataset.scrolled === 'true' && scrollTop > 0));
    if (this.dataset.scrolled !== scrolled) this.dataset.scrolled = scrolled;

    if (stickyMode === 'always') {
      this.dataset.scrollDirection = this.getBoundingClientRect().top >= 0
        ? 'none' : delta < 0 ? 'up' : 'down';
      return;
    }

    if (scrollTop === 0) {
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      this.#scrollDirection = 0;
      this.#directionStart = 0;
      return;
    }

    const isInteracting = this.matches(':focus-within') ||
      this.querySelector('[aria-expanded="true"], details[open], dialog[open]');
    if (isInteracting) {
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
      this.#scrollDirection = 0;
      this.#directionStart = scrollTop;
      return;
    }

    if (delta === 0) return;
    const direction = Math.sign(delta);
    if (direction !== this.#scrollDirection) {
      this.#scrollDirection = direction;
      this.#directionStart = previousScrollTop;
    }
    const travel = Math.abs(scrollTop - this.#directionStart);

    // Keep the same header visible while it compacts near the top. Once clear
    // of that area, require 24px of movement before hiding or revealing it.
    if ((scrollTop <= 80 && this.dataset.stickyState !== 'idle') || (direction < 0 && travel >= 24)) {
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    } else if (direction > 0 && scrollTop > 80 && travel >= 24) {
      this.dataset.stickyState = 'idle';
      this.dataset.scrollDirection = 'none';
    }
  };

  #handleFocusIn = () => {
    if (this.getAttribute('sticky') !== 'scroll-up' || this.dataset.stickyState !== 'idle') return;
    this.dataset.stickyState = 'active';
    this.dataset.scrollDirection = 'up';
    this.#scrollDirection = 0;
    this.#directionStart = getScrollTop();
  };

  connectedCallback() {
    super.connectedCallback();
    if (this.hasAttribute('data-media-hero-header')) this.#heroHeader = new ThemeHeaderHero(this);
    this.#resizeObserver.observe(this, { box: 'border-box' });
    this.#observeTopRow();
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.#lastScrollTop = getScrollTop();
    this.#directionStart = this.#lastScrollTop;

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      if (stickyMode === 'always') {
        this.#observeStickyPosition();
      } else if (stickyMode === 'scroll-up') {
        this.dataset.stickyState = this.#lastScrollTop > 80 ? 'idle' : 'inactive';
        // Reuse the position measured before the sticky-state style write.
        this.#updateScrollState(Math.max(0, this.#lastScrollTop));
      }

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  #observeTopRow() {
    // Existing markup repeats headerRowTop on both rows; the top class is unique.
    const topRow = this.querySelector('.header__row--top') ?? this.refs.headerRowTop;
    if (topRow === this.#topRow) return;
    if (this.#topRow) this.#resizeObserver.unobserve(this.#topRow);
    this.#topRow = topRow;
    this.#resizeObserver.observe(topRow, { box: 'border-box' });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeTopRow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#heroHeader?.disconnect();
    this.#heroHeader = null;
    this.#resizeObserver.disconnect();
    this.#topRow = null;
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.removeEventListener('focusin', this.#handleFocusIn);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      // Entries contain only changed boxes, not the complete group. Measuring
      // every sibling keeps announcement offsets stable while the header shrinks.
      const headerGroupHeight = calculateHeaderGroupHeight(header, headerGroup);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
